import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Clock, Gift, Loader2, ShieldOff, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/incident-watch/access`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
});

const defaultExpiry = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 12);
  return d.toISOString().slice(0, 10);
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export const IncidentWatchAccessManager = () => {
  const [data, setData] = useState({ people: [], paid_active: 0, comped_active: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', reason: '', expires: defaultExpiry() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { headers: authHeader() });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          email: form.email,
          name: form.name,
          reason: form.reason,
          expires_at: new Date(`${form.expires}T23:59:59Z`).toISOString(),
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        setNotice({ type: 'error', text: body.detail || 'Could not grant access.' });
      } else {
        setNotice({
          type: 'ok',
          text:
            body.status === 'updated'
              ? `Updated ${body.email} — access now runs to ${formatDate(body.expires_at)}.`
              : `${body.email} has access until ${formatDate(body.expires_at)}. It switches on when they sign in with that address.`,
        });
        setForm({ email: '', name: '', reason: '', expires: defaultExpiry() });
        load();
      }
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (person) => {
    if (!window.confirm(`Remove free access for ${person.email}?`)) return;
    await fetch(`${API}/${person.grant_id}/revoke`, { method: 'POST', headers: authHeader() });
    load();
  };

  return (
    <div className="w-full px-4 sm:px-6 pb-16" data-testid="iw-access-manager">
      <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
        <form
          onSubmit={submit}
          className="bg-white border border-slate-200 rounded-xl p-5 h-fit min-w-0"
          data-testid="iw-access-form"
        >
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Gift size={16} className="text-cyan-600" /> Give someone free access
          </h3>
          <p className="text-xs text-slate-500 mt-1 mb-4">
            They get the full subscriber view, incident alerts and the Monday digest — no charge. Works
            even if they&apos;ve never visited: access switches on when they sign in with this address.
          </p>

          <label className="block text-xs font-medium text-slate-700 mb-1">Email address</label>
          <Input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="name@example.com"
            data-testid="iw-access-email"
          />

          <label className="block text-xs font-medium text-slate-700 mb-1 mt-3">Name (optional)</label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Jane Smith"
            data-testid="iw-access-name"
          />

          <label className="block text-xs font-medium text-slate-700 mb-1 mt-3">Access ends</label>
          <Input
            type="date"
            required
            value={form.expires}
            onChange={(e) => setForm({ ...form, expires: e.target.value })}
            data-testid="iw-access-expires"
          />

          <label className="block text-xs font-medium text-slate-700 mb-1 mt-3">Why (optional)</label>
          <Input
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Local councillor"
            data-testid="iw-access-reason"
          />

          <Button type="submit" disabled={saving} className="w-full mt-4" data-testid="iw-access-submit">
            {saving ? <Loader2 size={15} className="animate-spin mr-2" /> : null}
            {saving ? 'Saving…' : 'Grant access'}
          </Button>

          {notice && (
            <p
              className={`text-xs mt-3 ${notice.type === 'ok' ? 'text-emerald-700' : 'text-red-600'}`}
              data-testid="iw-access-notice"
            >
              {notice.text}
            </p>
          )}
        </form>

        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <Badge variant="secondary" data-testid="iw-access-paid-count">
              {data.paid_active} paying
            </Badge>
            <Badge variant="secondary" data-testid="iw-access-comped-count">
              {data.comped_active} on free access
            </Badge>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Person</th>
                  <th className="text-left px-4 py-2 font-medium">Access</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Until</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody data-testid="iw-access-list">
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                      <Loader2 size={18} className="animate-spin inline" />
                    </td>
                  </tr>
                )}
                {!loading && data.people.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500" data-testid="iw-access-empty">
                      Nobody has access yet. Paying subscribers appear here automatically.
                    </td>
                  </tr>
                )}
                {data.people.map((p) => (
                  <tr key={`${p.kind}-${p.email}-${p.grant_id || ''}`} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900">{p.email || '(no email)'}</span>
                      {p.name ? <span className="block text-xs text-slate-500">{p.name}</span> : null}
                      {p.reason && p.kind === 'comped' ? (
                        <span className="block text-xs text-slate-400 italic">{p.reason}</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {p.kind === 'paid' ? (
                        <span className="text-slate-700">
                          {p.plan === 'incident_watch_annual' ? 'Annual · £49' : 'Monthly · £4.99'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-cyan-700">
                          <Gift size={13} /> Free access
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.active ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 size={14} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-500">
                          <XCircle size={14} /> Ended
                        </span>
                      )}
                      {p.kind === 'comped' && p.signed_in === false && p.active ? (
                        <span className="block text-xs text-amber-600 mt-0.5 inline-flex items-center gap-1">
                          <Clock size={12} /> awaiting first sign-in
                        </span>
                      ) : null}
                      {p.kind === 'paid' && p.cancel_at_period_end ? (
                        <span className="block text-xs text-amber-600 mt-0.5">cancelling at period end</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(p.expires_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {p.kind === 'comped' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revoke(p)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`iw-access-revoke-${p.email}`}
                        >
                          <ShieldOff size={14} className="mr-1" /> Remove
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-slate-500 mt-3">
            Paying subscribers are managed by Stripe — cancel or refund them in the Stripe dashboard, not here.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IncidentWatchAccessManager;
